/* eslint-disable max-len */
// Basic
import { useEffect, useState } from 'react';
// Router
import { Route, Routes, Navigate } from 'react-router-dom';
import RouteUrls from './enums/RouteUrls';
// Components
import Home from './pages/Home';
import CustomPayHome from './pages/CustomPayHome';
import Login from '@/app/modules/login/Login';
import OrganizationDetails from './pages/details/OrganizationDetails';
import Organizations from './pages/Organizations';
import UserDetails from './pages/details/UserDetails';
import PosTerminals from './pages/PosTerminals';
import CustomPayPosTerminals from './pages/CustomPayPosTerminals';
import SimCards from './pages/SimCards';
import RemoteCollections from './pages/RemoteCollections';
import ProtectedRoute from './components/ProtectedRoute';
import MerchantAccounts from './pages/MerchantAccounts';
import MerchantAccountDetails from './pages/details/MerchantAccountDetails';
import SubscriptionAndTerminalDetails from './pages/details/SubscriptionAndTerminalDetails';
import RemoteCollectionDetails from './pages/details/RemoteCollectionDetails';
import PushPayments from './pages/PushPayments';
import SimCardDetails from './pages/details/SimCardDetails';
import Plans from './pages/Plans';
import PlanDetails from './pages/details/PlanDetails';
import Spinner from '../../common-utils/components/renders/spinner/Spinner';
import Toaster from '../../common-utils/components/ui/toaster';
import PreLogin from '@/app/modules/preLogin/components/PreLogin';
import Users from './pages/Users';
import Envelopes from './pages/Envelopes';
import SearchContracts from './pages/SearchContracts';
import ModalDeactivationMfaConfirmation from '@/app/modules/session/components/modals/ModalDeactivationMfaConfirmation';
import ModalSecureAccess from '@/app/modules/session/components/modals/ModalSetMFA/ModalSecureAccess';
import { AppSidebar } from '../../common-utils/components/structure/sidebar/AppSidebar';
import SimCardGroupDetails from './pages/details/SimCardGroupDetails';
import CustomPaySimCards from './pages/CustomPaySimCards';
import PayluddCustomizationDetails from './pages/details/PayluddCustomizationDetails';
import WrapWithDomain from './components/WrapWithDomain';
import Maintenance from '../../common-utils/components/renders/maintenance/Maintenance';
import Historics from './pages/Historics';
import Documentation from './pages/Documentation';
import NewsLetter from './pages/NewsLetter';
import ModalReloadPage from '../modules/session/components/modals/ModalReloadPage';
import CustomPayEnvelopes from './pages/CustomPayEnvelopes';
import CustomPayMerchantAccountDetails from './pages/details/CustomPayMerchantAccountDetails';
// Store & queries
import useDeviceSizeStore from '../store/deviceSize';
import useSessionStore from '../store/session';
import useAssetsStore from '../store/assets';
import actionsOrganizations from '@/app/modules/organizations/api/actionsOrganizations';
import queryGetAssets from '@/app/modules/session/api/queries/getAssets';
import queryGetSession from '@/app/modules/session/api/queries/getSession';
import queryGetLastBuildData from '../modules/session/api/queries/getLastBuildData';
import { useQueries } from 'react-query';
// Hooks
import useDeviceType from './hooks/useDeviceType';
import useAssetsAndSession from './hooks/useAssetsAndSession';
import useMfaCheck from './hooks/useMfaCheck';
import useCustomPayStore from '../store/customPay';
import useReloadPage from './hooks/useReloadPage';
// Common
import setDomainInformationWithAssets from '@/common-utils/functions/setDomainInformationWithAssets';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import HiddenPermissions from '../permissions/HiddenPermissions';
// Style
import './Root.scss';


/**
 * Root
 * @returns Root Component or Loading Screen
 */
function Root() {

    // Constants
    const IS_MAINTENANCE = false;

    // State
    const [isLoading, setIsLoading] = useState(true);
    const [showModalDeactivationMfaConfirmation, setShowModalDeactivationMfaConfirmation] = useState(false);
    const [mfaDeactivationResponse, setMfaDeactivationResponse] = useState({});
    const [showModalSecureAccess, setShowModalSecureAccess] = useState(false);
    const [showModalReloadPage, setShowModalReloadPage] = useState(false);
    const [showNotificationUpdate, setShowNotificationUpdate] = useState(false);


    // Store
    const { session, domain, setDomain } = useSessionStore();
    const isSmallDevice = useDeviceSizeStore(s => s.isSmallDevice);
    const isCustomPay = useCustomPayStore(s => s.isCustomPay);
    const assets = useAssetsStore(s => s.assets);

    // API Calls
    const [getAssetsResponse, getSessionResponse, getLastBuildDataResponse] = useQueries([
        queryGetAssets(!IS_MAINTENANCE),
        queryGetSession(!IS_MAINTENANCE),
        queryGetLastBuildData(!IS_MAINTENANCE)
    ]);
    const getRootOrgaResponse = actionsOrganizations.getOrganization(session?.organization, false);

    // Hooks


    useAssetsAndSession(getAssetsResponse, getSessionResponse, getRootOrgaResponse, setIsLoading);
    useEffect(() => {
        if (!assets) { return; }

        setDomainInformationWithAssets({
            assets,
            error: [],
            setDomain,
            isCustomPay
        });
    }, [assets]);


    useDeviceType();

    useMfaCheck(showModalDeactivationMfaConfirmation, setShowModalDeactivationMfaConfirmation,
        setMfaDeactivationResponse, setShowModalSecureAccess, showModalSecureAccess);

    useReloadPage(getLastBuildDataResponse, showModalReloadPage, setShowModalReloadPage, showNotificationUpdate, setShowNotificationUpdate, isCustomPay);


    // Render
    if (IS_MAINTENANCE) {
        return <Maintenance />;
    }

    // Spinner - Waiting for initialization to finish before showing pages
    if (isLoading) {
        return <Spinner className='full-screen-loading' />;
    }

    return (
        <WrapWithDomain domain={domain} isSmallDevice={isSmallDevice} isCustomPay={isCustomPay}>
            <>
                {
                    session &&
                    <AppSidebar />
                }
                <Routes>

                    {isCustomPay || !hasRequiredPermissions({ permissions: session?.permissions, requiredPermissions: [HiddenPermissions.DASHBOARD_ACCESS.name] }) ? (
                        <Route exact index path={RouteUrls.HOME.url} element={<ProtectedRoute><CustomPayHome /></ProtectedRoute>} />
                    ) : (
                        <Route exact index path={RouteUrls.HOME.url} element={<ProtectedRoute><Home /></ProtectedRoute>} />
                    )}

                    <Route exact path={RouteUrls.TOKEN.url} element={<PreLogin />} />

                    <Route path={RouteUrls.LOGIN.url} element={<Login />} />

                    <Route exact path={RouteUrls.USERS.url}
                        element={<ProtectedRoute route={RouteUrls.USERS}><Users /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.USER_DETAILS.url}
                        element={<ProtectedRoute route={RouteUrls.USER_DETAILS}><UserDetails /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.MERCHANT_ACCOUNTS.url}
                        element={<ProtectedRoute route={RouteUrls.MERCHANT_ACCOUNTS}><MerchantAccounts /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.ORGANIZATIONS.url}
                        element={<ProtectedRoute route={RouteUrls.ORGANIZATIONS}><Organizations /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.ORGANIZATION_DETAILS.url}
                        element={<ProtectedRoute route={RouteUrls.ORGANIZATION_DETAILS}><OrganizationDetails /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.SEARCH_CONTRACTS.url}
                        element={<ProtectedRoute route={RouteUrls.SEARCH_CONTRACTS}><SearchContracts /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.PLANS.url}
                        element={<ProtectedRoute route={RouteUrls.PLANS}><Plans /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.PLAN_DETAILS.url}
                        element={<ProtectedRoute route={RouteUrls.PLAN_DETAILS}><PlanDetails /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.SUBSCRIPTION_DETAILS.url}
                        element={<ProtectedRoute route={RouteUrls.SUBSCRIPTION_DETAILS}><SubscriptionAndTerminalDetails /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.REMOTE_COLLECTION.url}
                        element={<ProtectedRoute route={RouteUrls.REMOTE_COLLECTION}><RemoteCollections /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.REMOTE_COLLECTION_TRANSACTIONS.url}
                        element={<ProtectedRoute route={RouteUrls.REMOTE_COLLECTION_TRANSACTIONS}><RemoteCollectionDetails /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.SIM_CARDS_DETAILS.url}
                        element={<ProtectedRoute route={RouteUrls.SIM_CARDS_DETAILS}><SimCardDetails /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.SIM_CARDS_GROUP_DETAILS.url}
                        element={<ProtectedRoute route={RouteUrls.SIM_CARDS_GROUP_DETAILS}><SimCardGroupDetails /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.HISTORICS.url}
                        element={<ProtectedRoute route={RouteUrls.HISTORICS}><Historics /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.DOCUMENTATION.url}
                        element={<ProtectedRoute route={RouteUrls.DOCUMENTATION}><Documentation /></ProtectedRoute>} />
                    <Route exact path={RouteUrls.NEWSLETTER.url}
                        element={<ProtectedRoute route={RouteUrls.NEWSLETTER}><NewsLetter /></ProtectedRoute>} />

                    {
                        isCustomPay ?
                            <>
                                <Route exact path={RouteUrls.SIM_CARDS.url}
                                    element={<ProtectedRoute route={RouteUrls.SIM_CARDS}><CustomPaySimCards /></ProtectedRoute>} />
                                <Route exact path={RouteUrls.SUBSCRIPTIONS.url}
                                    element={<ProtectedRoute route={RouteUrls.SUBSCRIPTIONS}><CustomPayPosTerminals /></ProtectedRoute>} />
                                <Route exact path={RouteUrls.ENVELOPES.url}
                                    element={<ProtectedRoute route={RouteUrls.ENVELOPES}><CustomPayEnvelopes /></ProtectedRoute>} />
                                <Route exact path={RouteUrls.MERCHANT_ACCOUNT_DETAILS.url}
                                    element={<ProtectedRoute route={RouteUrls.MERCHANT_ACCOUNT_DETAILS}><CustomPayMerchantAccountDetails /></ProtectedRoute>} />
                            </>
                            :
                            <>
                                <Route exact path={RouteUrls.PUSH_PAYMENTS.url}
                                    element={<ProtectedRoute route={RouteUrls.PUSH_PAYMENTS}><PushPayments /></ProtectedRoute>} />
                                <Route exact path={RouteUrls.SIM_CARDS.url}
                                    element={<ProtectedRoute route={RouteUrls.SIM_CARDS}>{<SimCards />}</ProtectedRoute>} />
                                <Route exact path={RouteUrls.SUBSCRIPTIONS.url}
                                    element={<ProtectedRoute route={RouteUrls.SUBSCRIPTIONS}><PosTerminals /></ProtectedRoute>} />
                                <Route exact path={RouteUrls.MERCHANT_ACCOUNT_DETAILS.url}
                                    element={<ProtectedRoute route={RouteUrls.MERCHANT_ACCOUNT_DETAILS}><MerchantAccountDetails /></ProtectedRoute>} />
                                <Route exact path={RouteUrls.ENVELOPES.url}
                                    element={<ProtectedRoute route={RouteUrls.ENVELOPES}><Envelopes /></ProtectedRoute>} />
                                <Route exact path={RouteUrls.PAYLUDD_CUSTOMIZATION_CREATION.url}
                                    element={<ProtectedRoute route={RouteUrls.PAYLUDD_CUSTOMIZATION_CREATION}><PayluddCustomizationDetails route={RouteUrls.PAYLUDD_CUSTOMIZATION_CREATION} /></ProtectedRoute>} />
                                <Route exact path={RouteUrls.PAYLUDD_CUSTOMIZATION_MANAGEMENT.url}
                                    element={<ProtectedRoute route={RouteUrls.PAYLUDD_CUSTOMIZATION_MANAGEMENT}><PayluddCustomizationDetails route={RouteUrls.PAYLUDD_CUSTOMIZATION_MANAGEMENT} /></ProtectedRoute>} />
                            </>
                    }

                    <Route path='*' element={<ProtectedRoute><Navigate to='/' /></ProtectedRoute>} />
                </Routes>
                {
                    showModalDeactivationMfaConfirmation &&
                    <ModalDeactivationMfaConfirmation
                        isOpen={showModalDeactivationMfaConfirmation}
                        setIsOpen={setShowModalDeactivationMfaConfirmation}
                        mfaDeactivationResponse={mfaDeactivationResponse}
                    />
                }
                {
                    showModalSecureAccess &&
                    <ModalSecureAccess
                        isOpen={showModalSecureAccess}
                        setIsOpen={setShowModalSecureAccess}
                    />
                }
                {
                    showModalReloadPage &&
                    <ModalReloadPage
                        isOpen={showModalReloadPage}
                        setIsOpen={setShowModalReloadPage}
                    />
                }
                <Toaster />
            </>
        </WrapWithDomain>
    );
}

export default Root;